import React, { useContext, useState } from "react";
import Select, { createFilter } from "react-select";
import AddIcon from "@mui/icons-material/Add";
import { Typography, makeStyles } from "@material-ui/core";
import FormError from "../../components/FormError";
import NewCaseField from "./Fields/NewCaseField";
import MaskField from "./Fields/MaskField";
import { ErrorMessage } from "formik";
import { viewportContext } from "../../context/ViewportContext";

import { useCaseStyles } from "./useCaseStyle";
import { reactSelectStyles } from "./reactSelectStyles";
import { CaseClientInfoTabProps } from "../../types/NewCaseView/CaseClientInfoTabTypes";
import DuplicateWarning, { DuplicateChecking } from "./DuplicateWarning";
import { useDuplicateCheck } from "../../hooks/NewCaseView/useDuplicateCheck";
import { generateQuickGuid, getPhoneMask } from "../shared/helpers/helper";
import { tokens } from "../../theme/tokens";
import AddRepresentativeDialog from "./AddRepresentativeDialog";

// Always the last row of the representative menu, so adding a new one is
// visible without having to type first.
const ADD_REP = "__add_representative__";
const matchesQuery = createFilter();
const repSelectStyles = {
  ...reactSelectStyles,
  option: (base: any, state: any) => {
    const style = reactSelectStyles.option(base, state);
    return state.data.value !== ADD_REP
      ? style
      : {
          ...style,
          display: "flex",
          alignItems: "center",
          gap: tokens.space[1],
          borderTop: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
          color: tokens.color.brand.primaryAction,
          fontWeight: tokens.type.denseStrong.weight,
        };
  },
};

const CaseClientInfoTab = ({
  clientList,
  clientSelect,
  fieldResourceSelect,
  setFieldValue,
  values,
}: CaseClientInfoTabProps) => {
  const classes = useCaseStyles();
  const { tallScreen } = useContext(viewportContext) as {
    tallScreen?: boolean;
  };
  const fileNoCheck = useDuplicateCheck(
    "client_file_no",
    values.case_details.client_information.client_file_no
  );
  const supervisorName = values.case_details.assigment_information.supervisor_name;
  const savedContacts =
    clientList.find((c) => c.client_id === values.client_id)?.contacts ?? [];
  const contactOptions = [
    ...savedContacts,
    ...(values.new_representative ? [values.new_representative] : []),
  ].map((c) => ({
    value: c.id,
    label: `${c.first_name} ${c.last_name}`.trim(),
    phone: c.phone,
  }));
  const selectedContact = contactOptions.find((o) => o.value === supervisorName);
  const [repQuery, setRepQuery] = useState("");
  const [addRepName, setAddRepName] = useState<string | null>(null);
  const phone = values.case_details.client_information.phone_number;
  const selectRepresentative = (id: string, repPhone: string) => {
    setFieldValue("case_details.assigment_information.supervisor_name", id);
    setFieldValue("case_details.client_information.phone_number", repPhone, false);
  };
  const addRepresentative = (first_name: string, last_name: string) => {
    const contact = {
      id: generateQuickGuid("client"),
      first_name,
      last_name,
      phone: "",
      email: "",
    };
    setFieldValue("new_representative", contact, false);
    selectRepresentative(contact.id, "");
    setAddRepName(null);
  };
  return (
    <div className={classes.columns}>
      <div className={classes.column}>
        <div className={classes.parent}>
          <div
            className={
              tallScreen ? classes.parentChildTallScreen : classes.parentChild
            }
          >
            <div className={classes.rowContainer}>
              <div className={classes.labelContainer}>
                <Typography>
                  Client Name<span className={classes.astrike}>*</span>
                </Typography>
              </div>
              <div className={classes.fieldContainer}>
                <Select
                  styles={reactSelectStyles}
                  value={
                    clientSelect.find((o) => o.value === values.client_id) ??
                    null
                  }
                  onChange={(e) => {
                    setFieldValue("client_id", e?.value ?? "", false);
                    // Representatives belong to a client, so a new client clears the pick.
                    setFieldValue("new_representative", undefined, false);
                    selectRepresentative("", "");
                  }}
                  options={clientSelect}
                />
              </div>
            </div>
            <ErrorMessage
              name="client_id"
              component={FormError}
              className={classes.error}
            />
          </div>
        </div>

        <div className={classes.parent}>
          <NewCaseField
            label="Client Loan#"
            type="text"
            name="case_details.client_information.client_loan"
          />
        </div>

        <div className={classes.parent}>
          <div
            className={
              tallScreen ? classes.parentChildTallScreen : classes.parentChild
            }
          >
            <div className={classes.rowContainer}>
              <div className={classes.labelContainer}>
                <Typography>
                  Client Representative
                  <span className={classes.astrike}>*</span>
                </Typography>
              </div>
              <div className={classes.fieldContainer}>
                <Select
                  styles={repSelectStyles}
                  value={selectedContact ?? null}
                  onChange={(e) =>
                    e?.value === ADD_REP
                      ? setAddRepName(repQuery)
                      : selectRepresentative(e?.value ?? "", e?.phone ?? "")
                  }
                  onInputChange={(text, { action }) => {
                    if (action === "input-change") setRepQuery(text);
                    else if (action === "input-blur" || action === "menu-close") setRepQuery("");
                  }}
                  options={[
                    ...contactOptions,
                    { value: ADD_REP, label: "Add new client representative", phone: "" },
                  ]}
                  filterOption={(option, input) =>
                    option.value === ADD_REP || matchesQuery(option, input)
                  }
                  formatOptionLabel={(o) =>
                    o.value === ADD_REP ? (
                      <>
                        <AddIcon sx={{ fontSize: tokens.icon.sm }} />
                        {o.label}
                      </>
                    ) : (
                      o.label
                    )
                  }
                  isDisabled={!values.client_id}
                  placeholder={
                    values.client_id ? "Select a representative" : "Select a client first"
                  }
                />
                {addRepName !== null && (
                  <AddRepresentativeDialog
                    initialName={addRepName}
                    onClose={() => setAddRepName(null)}
                    onAdd={addRepresentative}
                  />
                )}
              </div>
            </div>
            <ErrorMessage
              name="case_details.assigment_information.supervisor_name"
              component={FormError}
              className={classes.error}
            />
          </div>
        </div>

        <div className={classes.parent}>
          <MaskField
            label="Representative Phone Number"
            name="case_details.client_information.phone_number"
            setFieldValue={setFieldValue}
            value={phone}
            mask={getPhoneMask()}
            disabled={!supervisorName}
          />
        </div>
        {selectedContact && phone !== selectedContact.phone && (
          <Typography
            style={{
              fontSize: tokens.type.caption.size,
              color: tokens.color.text.secondary,
            }}
          >
            Saved to this client contact when the case is submitted.
          </Typography>
        )}
      </div>

      <div className={classes.column}>
        <div className={classes.parent}>
          <NewCaseField
            label="Client file number"
            type="text"
            name="case_details.client_information.client_file_no"
            labelAdornment={<DuplicateChecking active={fileNoCheck.checking} />}
          />
        </div>
        <DuplicateWarning matches={fileNoCheck.matches} label="Client File #" />

        <div className={classes.parent}>
          <NewCaseField
            label="User name"
            type="text"
            name="case_details.assigment_information.field_resource_name"
            disabled
          />
        </div>

        <div className={classes.parent}>
          <NewCaseField
            label="Event"
            type="text"
            name="case_details.assigment_information.event"
            disabled
          />
        </div>

        <div className={classes.parent}>
          <div
            className={
              tallScreen ? classes.parentChildTallScreen : classes.parentChild
            }
          >
            <div className={classes.rowContainer}>
              <div className={classes.labelContainer}>
                <Typography>Field Resource</Typography>
              </div>
              <div className={classes.fieldContainer}>
                <Select
                  styles={reactSelectStyles}
                  onChange={(e) =>
                    setFieldValue(
                      "case_details.field_resource_information[0].id",
                      e?.value ?? "",
                      false
                    )
                  }
                  options={fieldResourceSelect}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseClientInfoTab;
